import * as React from 'react';

import { enumUnit, Unit } from '@rematter/frontend-lib-api-client';
import {
  Button,
  NumberField,
  Select,
  Switch,
  TextField,
} from '@rematter/ui-web';
import { Trash2 } from 'lucide-react';

import { useTranslations } from '../../i18n/useTranslations';

import { SelectMaterial } from '~/shared/components/select-fields/SelectMaterial';

export type ProcessingTypeOutputItemData = {
  id: string;
  existingId?: string;
  itemId: string;
  outputUnit: Unit;
  yieldPercentage: string;
  isPrimary: boolean;
  isNoCost: boolean;
  costAllocationPercentage?: string;
  sortOrder: number;
  notes?: string;
};

type ProcessingTypeOutputItemRowProps = {
  item: ProcessingTypeOutputItemData;
  index: number;
  onUpdate: (
    index: number,
    data: Partial<ProcessingTypeOutputItemData>,
  ) => void;
  onRemove: (index: number) => void;
  isDisabled?: boolean;
};

const unitOptions = [
  { key: enumUnit.Pounds, label: 'LB' },
  { key: enumUnit.Kilograms, label: 'KG' },
  { key: enumUnit.MetricTons, label: 'MT' },
  { key: enumUnit.ShortTons, label: 'ST' },
  { key: enumUnit.Each, label: 'EA' },
];

export const ProcessingTypeOutputItemRow = ({
  item,
  index,
  onUpdate,
  onRemove,
  isDisabled = false,
}: ProcessingTypeOutputItemRowProps) => {
  const t = useTranslations();

  const handleItemChange = React.useCallback(
    (itemId?: string) => {
      onUpdate(index, { itemId: itemId || '' });
    },
    [index, onUpdate],
  );

  const handleYieldPercentageChange = React.useCallback(
    (value: number | null) => {
      onUpdate(index, { yieldPercentage: value?.toString() || '' });
    },
    [index, onUpdate],
  );

  const handleUnitChange = React.useCallback(
    (key: string | number) => {
      onUpdate(index, { outputUnit: key as Unit });
    },
    [index, onUpdate],
  );

  const handleIsPrimaryChange = React.useCallback(
    (value: boolean) => {
      onUpdate(index, { isPrimary: value });
    },
    [index, onUpdate],
  );

  const handleIsNoCostChange = React.useCallback(
    (value: boolean) => {
      onUpdate(index, { isNoCost: value });
    },
    [index, onUpdate],
  );

  const handleCostAllocationChange = React.useCallback(
    (value: number | null) => {
      onUpdate(index, {
        costAllocationPercentage: value !== null ? value.toString() : undefined,
      });
    },
    [index, onUpdate],
  );

  const handleNotesChange = React.useCallback(
    (value: string) => {
      onUpdate(index, { notes: value });
    },
    [index, onUpdate],
  );

  const handleRemove = React.useCallback(() => {
    onRemove(index);
  }, [index, onRemove]);

  return (
    <div className="@ProcessingTypeOutputItemRow grid grid-cols-[1fr_100px_100px_80px_80px_100px_1fr_40px] items-start gap-2 rounded border border-neutral/20 p-3">
      <SelectMaterial
        placeholder={t.processingTypesView.dialog.recipe.selectItem}
        value={item.itemId || undefined}
        onChange={handleItemChange}
        isDisabled={isDisabled}
      />
      <NumberField
        aria-label={t.processingTypesView.dialog.recipe.yieldPercentage}
        value={
          item.yieldPercentage ? parseFloat(item.yieldPercentage) : undefined
        }
        onChange={handleYieldPercentageChange}
        isDisabled={isDisabled}
        minValue={0}
        maxValue={100}
        formatOptions={{ style: 'percent', maximumFractionDigits: 2 }}
      />
      <Select
        placeholder={t.processingTypesView.dialog.recipe.outputUnit}
        value={item.outputUnit}
        onChange={handleUnitChange}
        options={unitOptions}
        isDisabled={isDisabled}
      />
      <div className="flex items-center justify-center pt-2">
        <Switch
          isSelected={item.isPrimary}
          onChange={handleIsPrimaryChange}
          isDisabled={isDisabled}
          aria-label={t.processingTypesView.dialog.recipe.isPrimary}
        />
      </div>
      <div className="flex items-center justify-center pt-2">
        <Switch
          isSelected={item.isNoCost}
          onChange={handleIsNoCostChange}
          isDisabled={isDisabled}
          aria-label={t.processingTypesView.dialog.recipe.isNoCost}
        />
      </div>
      <NumberField
        aria-label={t.processingTypesView.dialog.recipe.costAllocation}
        value={
          item.costAllocationPercentage
            ? parseFloat(item.costAllocationPercentage)
            : undefined
        }
        onChange={handleCostAllocationChange}
        isDisabled={isDisabled || item.isNoCost}
        minValue={0}
        maxValue={100}
        formatOptions={{ style: 'percent', maximumFractionDigits: 2 }}
      />
      <TextField
        placeholder={t.processingTypesView.dialog.recipe.notes}
        value={item.notes || ''}
        onChange={handleNotesChange}
        isDisabled={isDisabled}
      />
      <Button
        variant="ghost"
        color="failure"
        icon={<Trash2 size={16} />}
        onPress={handleRemove}
        isDisabled={isDisabled}
        aria-label={t.processingTypesView.dialog.recipe.removeOutput}
      />
    </div>
  );
};
